/**
 * @license
 * Copyright Paysafe 2026. All rights reserved.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Wallet } from '@paysafe/paysafe-wallet-saas-web/wallet';
import {
  WalletCheckoutGetSession,
  WalletCheckoutPaymentCancel, WalletCheckoutPaymentConfirm,
  WalletCheckoutPaymentCreate,
  WalletCheckoutPaymentPreview,
  WalletCheckoutPaymentResponse,
  WalletCheckoutService, WalletCheckoutSessionResponse
} from '@paysafe/paysafe-wallet-saas-web/wallet-checkout';

import { ScaDetails } from '../../models/payment/session-payment-response.model';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private sessionId: string | null = null;
  private secureCode: string | null = null;

  constructor(
    private readonly httpClient: HttpClient
  ) {
    // Dependency injection
  }

  init(params: { sessionId: string; secureCode: string }): void {
    this.sessionId = params.sessionId;
    this.secureCode = params.secureCode;
  }

  createPaymentPreview(): Promise<WalletCheckoutPaymentResponse> {
    const initData = this.ensureInitialized();
    if (!initData) {
      return Promise.reject(new Error('CheckoutService is not initialized.'));
    }

    const { secureCode, sessionId } = initData;
    return this.postCreatePayment(sessionId, secureCode);
  }

  updatePaymentStatus(
    status: 'PENDING' | 'PROCESSING' | 'CANCELLED',
    scaDetails?: Pick<ScaDetails, 'eventId' | 'walletOperationId'>
  ): Promise<WalletCheckoutPaymentResponse> {
    const initData = this.ensureInitialized();
    if (!initData) {
      return Promise.reject(new Error('CheckoutService is not initialized.'));
    }

    const { secureCode, sessionId } = initData;
    const walletCheckoutService: WalletCheckoutService = Wallet.getInstance().getWalletCheckoutService();

    switch (status) {
      case 'PENDING': {
        const paymentCreate: WalletCheckoutPaymentCreate = {
          sessionId,
          secureCode
        };
        return walletCheckoutService.createPayment(paymentCreate);
      }
      case 'PROCESSING': {
        const paymentConfirm: WalletCheckoutPaymentConfirm = {
          sessionId,
          secureCode,
          ...(scaDetails ? { scaDetails } : {})
        };
        return walletCheckoutService.confirmPayment(paymentConfirm);
      }
      case 'CANCELLED': {
        const paymentCancel: WalletCheckoutPaymentCancel = {
          sessionId,
          secureCode
        };
        return walletCheckoutService.cancelPayment(paymentCancel);
      }
      default: {
        return Promise.reject(new Error('Invalid payment status.'));
      }
    }
  }

  getSession(): Promise<WalletCheckoutSessionResponse> {
    const initData = this.ensureInitialized();
    if (!initData) {
      return Promise.reject(new Error('CheckoutService is not initialized.'));
    }

    const { sessionId, secureCode } = initData;
    const walletCheckoutService: WalletCheckoutService = Wallet.getInstance().getWalletCheckoutService();
    const getSessionRequest: WalletCheckoutGetSession = {
      secureCode
    };
    return walletCheckoutService.getSession(sessionId, getSessionRequest);
  }

  private ensureInitialized(): { sessionId: string; secureCode: string } | null {
    if (!this.sessionId || !this.secureCode) {
      return null;
    }
    return {
      sessionId: this.sessionId,
      secureCode: this.secureCode,
    };
  }

  private postCreatePayment(
    sessionId: string,
    secureCode: string
  ): Promise<WalletCheckoutPaymentResponse> {
    const paymentPreview: WalletCheckoutPaymentPreview = {
      sessionId,
      secureCode
    };
    const walletCheckoutService: WalletCheckoutService = Wallet.getInstance().getWalletCheckoutService();
    return walletCheckoutService.previewPayment(paymentPreview);
  }
}
